import { FC } from 'react';
import { Modal } from 'antd';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

export interface Tutorial {
  id: string;
  title: string;
  description: string;
  gif: string;
  vimeoId: string;
}

export const TUTORIALS: Tutorial[] = [
  {
    id: 'show-all-requests',
    title: 'View all requests',
    description: 'See every request across your account.',
    gif: '/how-to/GIF/SGM_MicroVideosLQ_ShowAllRequests_03.17_V1.gif',
    vimeoId: '1181177419',
  },
  {
    id: 'search-requests',
    title: 'Search requests',
    description: 'Find any request using the search bar.',
    gif: '/how-to/GIF/SGM_MicroVideosLQ_SearchRequests_03.17_V1.gif',
    vimeoId: '1181177391',
  },
  {
    id: 'duplicate-request',
    title: 'Duplicate a request',
    description: 'Reuse an existing request instead of starting from scratch.',
    gif: '/how-to/GIF/SGM_MicroVideosLQ_DuplicateRequest_03.17_V1.gif',
    vimeoId: '1181177180',
  },
  {
    id: 'upload-file',
    title: 'Upload a file',
    description: 'Attach assets or briefs directly to any request.',
    gif: '/how-to/GIF/SGM_MicroVideosLQ_UploadFile_03.17_V1.gif',
    vimeoId: '1181177450',
  },
  {
    id: 'find-deliverables',
    title: 'Find deliverables',
    description: 'See what is done and what is still in progress.',
    gif: '/how-to/GIF/SGM_MicroVideosLQ_FindDeliverables_03.17_V1.gif',
    vimeoId: '1181177242',
  },
  {
    id: 'chat-savage',
    title: 'Chat with Savage',
    description: 'Message your team directly inside the request.',
    gif: '/how-to/GIF/SGM_MicroVideosLQ_ChatSavage_03.17_V1.gif',
    vimeoId: '1181177061',
  },
  {
    id: 'edit-comment',
    title: 'Edit a comment',
    description: 'Update your comment after posting.',
    gif: '/how-to/GIF/SGM_MicroVideosLQ_EditComment_03.17_V1.gif',
    vimeoId: '1181177211',
  },
  {
    id: 'delete-comment',
    title: 'Delete a comment',
    description: 'Remove comments you no longer need.',
    gif: '/how-to/GIF/SGM_MicroVideosLQ_DeleteComment_03.17_V1.gif',
    vimeoId: '1181177108',
  },
  {
    id: 'find-mentions',
    title: 'Find mentions',
    description: 'See everywhere you have been tagged across requests.',
    gif: '/how-to/GIF/SGM_MicroVideosLQ_FindMentions_03.17_V1.gif',
    vimeoId: '1181177268',
  },
  {
    id: 'find-notifications',
    title: 'Find notifications',
    description: 'Stay on top of updates, approvals, and replies.',
    gif: '/how-to/GIF/SGM_MicroVideosLQ_FindNotifications_03.17_V1.gif',
    vimeoId: '1181177305',
  },
  {
    id: 'find-resources',
    title: 'Find resources',
    description: 'Access any file or resource that has been shared.',
    gif: '/how-to/GIF/SGM_MicroVideosLQ_FindResources_04.06_V2.gif',
    vimeoId: '1181177332',
  },
  {
    id: 'delete-team-members',
    title: 'Remove a team member',
    description: 'Remove people who no longer need access.',
    gif: '/how-to/GIF/SGM_MicroVideosLQ_DeleteTeamMembers_03.17_V1.gif',
    vimeoId: '1181177147',
  },
  {
    id: 'report-bug',
    title: 'Report a bug',
    description: 'Something off? Let us know and we will fix it fast.',
    gif: '/how-to/GIF/SGM_MicroVideosLQ_ReportBug_03.17_V1.gif',
    vimeoId: '1181177357',
  },
  {
    id: 'change-password',
    title: 'Change your password',
    description: 'Update your credentials to keep your account secure.',
    gif: '/how-to/GIF/SGM_MicroVideosLQ_ChangePassword_04.06_V2.gif',
    vimeoId: '1181177027',
  },
];

const TutorialModal: FC = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();

  const featureId = searchParams.get('feature');
  const selectedTutorial = TUTORIALS.find((t) => t.id === featureId) ?? null;

  return (
    <Modal
      open={!!selectedTutorial}
      onCancel={() => navigate(pathname, { preventScrollReset: true })}
      footer={null}
      destroyOnClose
      width={1280}
      title={selectedTutorial?.title}
      centered
    >
      {selectedTutorial && (
        <div className="pt-2">
          <div style={{ padding: '55.3% 0 0 0', position: 'relative' }}>
            <iframe
              src={`https://player.vimeo.com/video/${selectedTutorial.vimeoId}?badge=0&autopause=0&player_id=0&app_id=58479&autoplay=1`}
              frameBorder={0}
              allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
              title={selectedTutorial.title}
            />
          </div>
          {selectedTutorial.description && (
            <p className="mt-3 text-sm text-gray-500">{selectedTutorial.description}</p>
          )}
        </div>
      )}
    </Modal>
  );
};

export default TutorialModal;
